import React from 'react'
import { TaskTimeFrame } from 'components/TasksV2/TaskCreation/model/types'
import useLocaleText from 'components/useLocaleText'
import dayjs from 'dayjs'
import localeData from 'dayjs/plugin/localeData'
import 'dayjs/locale/en'
import { FrequencyType } from 'components/NewsFeed/PollTaskForm/utils'
import { Box, Flex, Text } from '@chakra-ui/core'
import { Repeat } from 'lucide-react'
import { StepType, useFormStepContext } from 'components/TasksV2/TaskCreation/FormStepContext'
import { DAYS_OF_WEEK_FULL } from '../../TimeFrameSelection/FrequencyDatesPicker/utils'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import { useDateLocale } from 'hooks/useDateLocale'
import { calculateRecurrenceDeadline, calculateTots } from 'components/TasksV2/TaskCreation/utils'
import keys from 'constants/keys'
import { useFormContext } from 'react-hook-form'

dayjs.extend(localeData)

interface SummaryProps {
  fromTs: string
  toTs: string
  locale: string
  translations?: Object
  daysOfWeek?: number[]
  t_from?: string
  t_to: string
  t_rec_sum_start_date: string
  t_rec_sum_end_date: string
  recurrenceDeadline?: string | null
  recurringText: string
  is_support_us_date_format?: boolean
}

export const Summery: React.FC<{ timeFrame?: Partial<TaskTimeFrame> }> = ({ timeFrame }) => {
  const { formData } = useFormStepContext()
  const { watch } = useFormContext()

  const timeFrameFormData = timeFrame ?? formData[StepType.TIME_FRAME_SELECTION]

  const locale = useSelector((state: RootState) => state.general.locale)
  const translations = useSelector((state: RootState) => state.config.translations)
  const theme = useSelector((state: RootState) => state.general.theme)

  const isLocaleReady = useDateLocale(locale)

  const t_choose_dates = useLocaleText('t_choose_dates')
  const t_to = useLocaleText('t_to')
  const t_from = useLocaleText('t_from')
  const t_sum_start_date = useLocaleText('t_sum_start_date')
  const t_sum_end_date = useLocaleText('t_sum_end_date')
  const t_rec_sum_start_date = useLocaleText('t_rec_sum_start_date')
  const t_rec_sum_end_date = useLocaleText('t_rec_sum_end_date')
  const t_daily_recurring = useLocaleText('t_daily_recurring')
  const t_monthly_recurring = useLocaleText('t_monthly_recurring')
  const t_weekly_recurring = useLocaleText('t_weekly_recurring')
  const t_days_recurring = useLocaleText('t_days_recurring')
  const t_start_date = useLocaleText('t_start_date')
  const t_end_date = useLocaleText('t_end_date')

  const isRecurring = timeFrameFormData?.is_recurring ?? false
  const fromTs = getFormattedDate(timeFrameFormData?.from_ts || watch('from_ts'))
  const deadline = getFormattedDate(timeFrameFormData?.deadline || watch('deadline'))
  const toTs = calculateTots({ is_recurring: isRecurring, deadline, formattedDeadline: deadline })
  const daysOfWeek = timeFrameFormData?.days_of_week as number[]
  const frequency = timeFrameFormData?.recurrence_frequency ?? null

  const canDescribeRecurrence =
    !!timeFrameFormData?.from_ts &&
    dayjs(timeFrameFormData.from_ts).isValid() &&
    !!timeFrameFormData?.deadline &&
    dayjs(timeFrameFormData.deadline).isValid() &&
    /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(timeFrameFormData?.frequency_time || '') &&
    (frequency === FrequencyType.MONTH
      ? (timeFrameFormData?.monthly_range?.length || 0) >= 2 &&
        timeFrameFormData!.monthly_range!.every((date) => dayjs(date).isValid())
      : (daysOfWeek?.length || 0) >= (frequency === FrequencyType.WEEK ? 2 : 1) &&
        daysOfWeek.every((day) => Number.isInteger(day) && day >= 0 && day <= 6))

  const recurrenceDeadline =
    isRecurring && canDescribeRecurrence
      ? calculateRecurrenceDeadline({
          is_recurring: timeFrameFormData?.is_recurring ?? false,
          recurrence_frequency: timeFrameFormData?.recurrence_frequency ?? null,
          from_ts: timeFrameFormData?.from_ts || watch('from_ts'),
          frequency_time: timeFrameFormData?.frequency_time ?? '',
          monthly_range: timeFrameFormData?.monthly_range ?? [],
          days_of_week: timeFrameFormData?.days_of_week ?? null,
        })
      : ''

  const renderSummaryContent = () => {
    if (frequency === FrequencyType.DAY) {
      return renderDailySummary({
        fromTs,
        toTs,
        daysOfWeek,
        locale,
        translations,
        t_from,
        t_to,
        t_rec_sum_start_date,
        t_rec_sum_end_date,
        recurringText: daysOfWeek?.length === 7 ? t_daily_recurring : t_days_recurring,
        is_support_us_date_format: theme?.is_support_us_date_format,
      })
    }
    if (frequency === FrequencyType.WEEK) {
      return renderWeeklySummary({
        fromTs,
        toTs,
        daysOfWeek,
        locale,
        translations,
        t_from,
        t_to,
        t_rec_sum_start_date,
        t_rec_sum_end_date,
        recurringText: t_weekly_recurring,
        recurrenceDeadline,
        is_support_us_date_format: theme?.is_support_us_date_format,
      })
    }
    if (frequency === FrequencyType.MONTH) {
      return renderMonthlySummary({
        fromTs,
        toTs,
        locale,
        t_from,
        t_to,
        t_rec_sum_start_date,
        t_rec_sum_end_date,
        recurringText: t_monthly_recurring,
        recurrenceDeadline,
        is_support_us_date_format: theme?.is_support_us_date_format,
      })
    }
    return renderDefaultSummary({
      fromTs,
      deadline,
      locale,
      t_sum_start_date,
      t_sum_end_date,
      t_start_date,
      t_end_date,
      is_support_us_date_format: theme?.is_support_us_date_format,
    })
  }

  if (!isLocaleReady) {
    return null
  }

  if (isRecurring && !canDescribeRecurrence) return <>{t_choose_dates}</>

  if (isRecurring) {
    return (
      <Flex flexDir="column">
        <Text>
          <Repeat
            size={14}
            strokeWidth={3}
            style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }}
          />
          {renderSummaryContent()}
        </Text>
      </Flex>
    )
  }

  return (
    <Flex flexDir="column" w="100%">
      {renderSummaryContent()}
    </Flex>
  )
}

// Helper Functions
const getFormattedDate = (date: string): string => dayjs(date).format(keys.DEFAULT_STRING_FORMAT_TIMESTAMP)

const renderDailySummary: React.FC<SummaryProps> = ({
  fromTs,
  toTs,
  daysOfWeek,
  locale,
  translations,
  t_from,
  t_to,
  t_rec_sum_start_date,
  t_rec_sum_end_date,
  recurringText,
  is_support_us_date_format,
}) => {
  const isUsLocale = is_support_us_date_format

  const dateFormat = isUsLocale ? 'MMMM DD YYYY' : 'DD MMMM YYYY'
  const timeFormat = isUsLocale ? 'hh:mm A' : 'HH:mm'

  return (
    <Text as="span" lineHeight={1.8}>
      {t_rec_sum_start_date} {dayjs(fromTs).locale(locale).format(dateFormat)} {recurringText}{' '}
      {daysOfWeek?.length === 7
        ? ''
        : daysOfWeek?.map((day) => translations?.[locale]?.[DAYS_OF_WEEK_FULL[day]]).join(', ')}{' '}
      {t_from} {dayjs(fromTs).locale(locale).format(timeFormat)} {t_to} {dayjs(toTs).locale(locale).format(timeFormat)}.{' '}
      <br />
      {t_rec_sum_end_date} {dayjs(toTs).locale(locale).format(dateFormat)}
    </Text>
  )
}

const renderWeeklySummary: React.FC<SummaryProps> = ({
  fromTs,
  toTs,
  daysOfWeek,
  locale,
  translations,
  t_to,
  t_rec_sum_start_date,
  t_rec_sum_end_date,
  recurringText,
  recurrenceDeadline,
  is_support_us_date_format,
}) => {
  const isUsLocale = is_support_us_date_format

  const dateFormat = isUsLocale ? 'MMMM DD YYYY' : 'DD MMMM YYYY'
  const timeFormat = isUsLocale ? 'hh:mm A' : 'HH:mm'

  return (
    <Text as="span" lineHeight={1.8}>
      {t_rec_sum_start_date} {dayjs(fromTs).locale(locale).format(dateFormat)} {recurringText}{' '}
      {translations?.[locale]?.[DAYS_OF_WEEK_FULL[daysOfWeek![0]]]} {dayjs(fromTs).locale(locale).format(timeFormat)}{' '}
      {t_to} {translations?.[locale]?.[DAYS_OF_WEEK_FULL[daysOfWeek![1]]]}{' '}
      {dayjs(recurrenceDeadline).locale(locale).format(timeFormat)}. <br />
      {t_rec_sum_end_date} {dayjs(toTs).locale(locale).format(dateFormat)}
    </Text>
  )
}

const renderMonthlySummary: React.FC<SummaryProps> = ({
  fromTs,
  toTs,
  locale,
  t_to,
  t_rec_sum_start_date,
  t_rec_sum_end_date,
  recurringText,
  recurrenceDeadline,
  is_support_us_date_format,
}) => {
  const isUsLocale = is_support_us_date_format

  const dateFormat = isUsLocale ? 'MMMM DD YYYY' : 'DD MMMM YYYY'
  const timeFormat = isUsLocale ? 'hh:mm A' : 'HH:mm'

  return (
    <Text as="span" lineHeight={1.8}>
      {t_rec_sum_start_date} {dayjs(fromTs).locale(locale).format(dateFormat)} {recurringText}{' '}
      {dayjs(fromTs).locale(locale).format(timeFormat)} {t_to}{' '}
      {dayjs(recurrenceDeadline).locale(locale).format(timeFormat)}. <br />
      {t_rec_sum_end_date} {dayjs(toTs).locale(locale).format(dateFormat)}
    </Text>
  )
}

const renderDefaultSummary: React.FC<{
  fromTs: string
  deadline: string
  locale: string
  t_sum_start_date: string
  t_sum_end_date: string
  t_start_date: string
  t_end_date: string
  is_support_us_date_format?: boolean
}> = ({ fromTs, deadline, locale, t_start_date, t_end_date, is_support_us_date_format }) => {
  const format = is_support_us_date_format ? 'MMM D, hh:mm A' : 'D MMM, HH:mm'

  return (
    <Flex flexDir="column" w="100%">
      {fromTs && (
        <Flex justifyContent="space-between" alignItems="center" py="8px">
          <Text color="gray.500">{t_start_date}</Text>
          <Text>{dayjs(fromTs).locale(locale).format(format)}</Text>
        </Flex>
      )}
      {fromTs && deadline && <Box h="1px" bg="gray.100" />}
      {deadline && (
        <Flex justifyContent="space-between" alignItems="center" py="8px">
          <Text color="gray.500">{t_end_date}</Text>
          <Text>{dayjs(deadline).locale(locale).format(format)}</Text>
        </Flex>
      )}
    </Flex>
  )
}
